import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useVersion, useDataProvider } from 'react-admin';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import { GroupContent, GroupItem } from 'components/common/Group';
import { LabeledPriceField } from 'components/fields';
import SinglePayAmount from '../SinglePayAmount';

import SubmitButton from 'components/buttons/SubmitButton';
import formatPrice from 'services/helpers/formatPrice';
import AutoRefillTab from '../AutoRefillTab';
import { Typography } from '@material-ui/core';
import SavedBankAccounts from '../SavedBankAccounts';
import useCustomNotify from 'hooks/useCustomNotify';
import { FormErrorLabel } from 'components/form';

const useStyles = makeStyles((theme) => ({
  calcBlock: {
    padding: theme.spacing(0, 3, 2),
  },

  calcRow: {
    padding: theme.spacing(1, 0),
  },

  calcTotal: {
    fontWeight: 'bold',
    fontSize: 20,
  },
  error: {
    fontSize: 14,
    color: theme.palette.error.main,
    padding: theme.spacing(0, 0, 0.5, 3),
  },
  actions: {
    padding: '32px 24px',

    '& .MuiButton-containedSecondary': {
      backgroundColor: theme.palette.secondary.light,

      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },
  dialogButton: {
    textTransform: 'none',
  },
  dialogBody: {
    maxWidth: '560px',
    margin: '0 auto',
  },
  tabs: {
    padding: theme.spacing(0, 2),
    marginBottom: '20px',
  },
  errorText: {
    padding: theme.spacing(0, 0, 0.5, 3),
  },
}));

const ADD_BALANCE = 'Add balance';
const AUTO_REFILL_TAB = 'Auto-Refill';
const SINGLE_PAYMENT_TAB = 'Single Payment';
const NEW_ACCOUNT = 'NEW_ACCOUNT';
const ADD_ACCOUNT = 'Add new account';

let timeoutId;
const TIMEOUT = 10000;

const formatAutoRefillAmount = (amount, type) => {
  if (!type) {
    return amount.substr(12);
  } else {
    return 'AUTO_REFILL_' + amount;
  }
};

const formatAmount = (amount) =>
  formatPrice(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const ACHInner = ({
  setPageTitle,
  callback,
  paymentOptions,
  paymentOptionsLoading,
  paymentLimits,
  paymentLoader,
  errorAmount,
  getBillingData,
  amount,
  setAmount,
  currentAutoRefillStatus,
  setErrorAmount,
}: any) => {
  const classes = useStyles();
  const location = useLocation();
  const version = useVersion();
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();
  const stripe = useStripe();

  const [pageType, setPageType] = useState<string>(AUTO_REFILL_TAB);

  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [settingsError, setSettingsError] = useState<string>('');
  const [processing, setProcessing] = useState<boolean>(false);

  const [autoRefillAmount, setAutoRefillAmount] = useState(100);
  const [enabledAutoRefill, setEnabledAutoRefill] = useState(currentAutoRefillStatus);
  const [failedAccount, setFailedAccount] = useState<string>('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [pickedAccount, setPickedAccount] = useState<string>(NEW_ACCOUNT);
  const [removingAccount, setRemovingAccount] = useState<string>('');
  const [defaultAccount, setDefaultAccount] = useState<string>(NEW_ACCOUNT);
  const [autoRefillAccount, setAutoRefillAccount] = useState<string>(NEW_ACCOUNT);

  const openSingleTabByDefault = useSelector(
    (state) => state.refill.openSingleTabByDefault,
    shallowEqual
  );

  useEffect(() => {
    if (openSingleTabByDefault) setPageType(SINGLE_PAYMENT_TAB);
  }, [openSingleTabByDefault]);

  const handleClose = () => {
    setErrorAmount(0);
    callback();
  };

  const handleData = async () => {
    try {
      setSettingsLoading(true);
      const billingData = await getBillingData();

      setBankAccounts(billingData.usBankAccounts);

      if (billingData.autoRefill.enabled) {
        setAutoRefillAmount(formatAutoRefillAmount(billingData.autoRefill.amount, false));
      }

      setDefaultAccount(billingData.defaultUsBankAccount);

      const bankAutoRefillEnabled =
        billingData.autoRefill.enabled &&
        billingData.autoRefill.paymentMethodType === 'US_BANK_ACCOUNT';

      setEnabledAutoRefill(bankAutoRefillEnabled || currentAutoRefillStatus);
      const selectedAccountId = billingData.autoRefill.defaultUsBankAccount;

      const selectedAccount = billingData.usBankAccounts?.find(
        (account) => account.paymentMethodId === selectedAccountId
      );

      setAutoRefillAccount(selectedAccount?.paymentMethodId ?? NEW_ACCOUNT);

      if (billingData.autoRefill.enabled) {
        setFailedAccount(selectedAccount?.failedLastTime ? selectedAccount?.lastDigits : '');
      } else {
        setFailedAccount('');
      }
    } catch (error: any) {
      setSettingsError(error?.message ?? error);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    handleData();
  }, [version]); //eslint-disable-line

  const updateAutoRefill = async (enabled: boolean, amount: number, accountId: string) => {
    try {
      if (bankAccounts?.length > 0) {
        const result = await dataProvider.put('billingSettings', {
          data: {
            autoRefill: {
              enabled: enabled,
              amount: 'AUTO_REFILL_' + amount,
              defaultUsBankAccount: accountId,
              paymentMethodType: 'US_BANK_ACCOUNT',
            },
          },
        });

        if (result?.data?.autoRefill) {
          setEnabledAutoRefill(!!result.data.autoRefill.enabled);
        }

        setDefaultAccount(accountId);

        notify('Auto-Refill setting updated!', 'success');
        handleClose();
      } else {
        throw new Error('Please add a payment account!');
      }
    } catch (error: any) {
      if (error.message.includes('manual review')) {
        notify(
          'This payment requires a manual review. Please allow up to 1 business day. Check status under Deposit tab.',
          'error'
        );
        handleClose();

        return;
      }

      if (error?.message?.includes('Bank Transfer - Auto-refill')) {
        notify(error?.message, 'error');
        handleClose();

        return;
      }

      return { [FORM_ERROR]: error.message };
    }
  };

  const openNewAccountPage = (amount: number) => {
    setAmount(amount);
    setPageTitle(ADD_BALANCE);
    return;
  };

  const resetTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  const setPaymentTimeout = () =>
    setTimeout(() => {
      resetTimeout();
      setProcessing(false);
      handleClose();
    }, TIMEOUT);

  const getPaymentReview = async (id: string) => {
    try {
      const {
        data: { achUserMessage, status },
      } = await dataProvider.getOne('paymentReview', { id });

      if (status === 'processing' && achUserMessage) {
        notify(achUserMessage);
        setProcessing(false);
        resetTimeout();
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));

      return timeoutId ? await getPaymentReview(id) : null;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while getting payment review';
      notify(errorMessage, 'error');
    }
  };

  const makePaymentWithSavedAccount = async (accountId: string, customAmount: number) => {
    try {
      if (!stripe) {
        throw Error("Stripe hasn't been loaded");
      }

      setProcessing(true);

      const {
        data: { clientKey, id },
      } = await dataProvider.post('paymentIntents', {
        data: {
          paymentMethodId: accountId,
          customAmount,
          paymentMethodType: 'US_BANK_ACCOUNT',
        },
      });

      const stripeResponse = await stripe.confirmUsBankAccountPayment(clientKey, {
        payment_method: accountId,
      });

      if (stripeResponse.error) throw stripeResponse.error;

      timeoutId = setPaymentTimeout();

      await getPaymentReview(id);

      handleClose();

      return stripeResponse;
    } catch (error: any) {
      if (error?.message?.includes('instant availability')) {
        handleClose();
        notify(error?.message, 'error');
        return;
      }

      return { [FORM_ERROR]: error?.message ?? error };
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitAch = (values) => {
    try {
      setErrorAmount(0);

      if (pageType === SINGLE_PAYMENT_TAB) {
        if (pickedAccount === NEW_ACCOUNT) {
          return openNewAccountPage(parseFloat(values.customAmount));
        }

        if (pickedAccount === '') {
          return makePaymentWithSavedAccount(defaultAccount, values.customAmount);
        } else {
          return makePaymentWithSavedAccount(pickedAccount, values.customAmount);
        }
      }

      if (pageType === AUTO_REFILL_TAB) {
        const autoAccount = !pickedAccount ? autoRefillAccount : pickedAccount;

        if (autoAccount === NEW_ACCOUNT) {
          enabledAutoRefill ? setPageTitle(ADD_ACCOUNT) : handleClose();
          return;
        }

        return updateAutoRefill(enabledAutoRefill, autoRefillAmount, autoAccount);
      }
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    }
  };

  const removeAccount = async (id: string) => {
    try {
      setRemovingAccount(id);

      await dataProvider.delete('removeCard', { id });
      if (pickedAccount === id) {
        setPickedAccount('');
      }

      await handleData();
      notify('Account removed!', 'success');
    } catch (error: any) {
    } finally {
      setRemovingAccount('');
    }
  };

  const initialValues = {
    paymentOption: null,
    name: null,
    customAmount: null,
  };

  const handleChangeTab = (form) => (event, value) => {
    form.reset(initialValues);
    setPageType(value);
  };

  const changeAutoRefillAmount = (e) => {
    setAutoRefillAmount(e.target.value);
  };

  return (
    <Box className={classes.dialogBody}>
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmitAch}
        render={({ values, form, handleSubmit, submitting, ...renderProps }) => {
          const handleChangeAmount = (value) => {
            const convertedValue = parseFloat(value);
            const matchOption = paymentOptions?.find(
              (option) => option.baseAmount === convertedValue
            );

            if (matchOption) {
              form.batch(() => {
                form.change('paymentOption', { ...matchOption, stripeFee: 0, totalAmount: value });
                form.change('customAmount', value);
              });
              return;
            }

            form.batch(() => {
              form.change('paymentOption', {
                option: null,
                baseAmount: convertedValue,
                stripeFee: 0,
                totalAmount: convertedValue,
              });
              form.change('customAmount', value);
            });
          };

          if (amount) {
            handleChangeAmount(amount);
            setAmount(0);
          }

          if (errorAmount && !values.customAmount && typeof values.customAmount !== 'string') {
            handleChangeAmount(errorAmount.toFixed(2));
          }

          return (
            <>
              <form onSubmit={handleSubmit}>
                {settingsError && (
                  <div className={classes.errorText}>
                    <Typography variant="body1" color="error">
                      {settingsError}
                    </Typography>
                  </div>
                )}
                {!settingsError && !settingsLoading && (
                  <>
                    <Tabs
                      className={classes.tabs}
                      value={pageType}
                      indicatorColor="primary"
                      textColor="primary"
                      onChange={handleChangeTab(form)}>
                      <Tab value={AUTO_REFILL_TAB} label="Auto-Refill" />
                      <Tab value={SINGLE_PAYMENT_TAB} label="Single Payment" />
                    </Tabs>

                    {pageType === AUTO_REFILL_TAB && (
                      <>
                        <AutoRefillTab
                          enabledAutoRefill={enabledAutoRefill}
                          failedCard={failedAccount}
                          autoRefillAmount={autoRefillAmount}
                          changeAutoRefillAmount={changeAutoRefillAmount}
                          setEnabledAutoRefill={setEnabledAutoRefill}
                        />
                        {enabledAutoRefill && !!bankAccounts && (
                          <SavedBankAccounts
                            bankAccounts={bankAccounts}
                            pickedAccount={pickedAccount}
                            setPickedAccount={setPickedAccount}
                            removeAccount={removeAccount}
                            removing={removingAccount}
                            defaultAccount={autoRefillAccount}
                          />
                        )}
                      </>
                    )}

                    {pageType === SINGLE_PAYMENT_TAB && (
                      <>
                        {!!errorAmount && location.pathname.includes('/orders/groups') && (
                          <div className={classes.error}>
                            Please add at least {formatPrice(errorAmount)} funds or enable
                            auto-refill to complete this transaction.
                          </div>
                        )}

                        {!paymentOptionsLoading && paymentOptions?.length > 0 && (
                          <SinglePayAmount
                            options={paymentOptions?.map((option) => ({
                              label: formatAmount(option.baseAmount),
                              value: option,
                            }))}
                            form={form}
                            paymentLimits={paymentLimits}
                            values={values}
                            handleChangeAmount={handleChangeAmount}
                            isAch
                          />
                        )}
                        {values.paymentOption && (
                          <GroupContent className={classes.calcBlock}>
                            <GroupItem wide>
                              <LabeledPriceField
                                classes={{ root: classes.calcRow }}
                                label="Payment processing fee"
                                record={values}
                                source="paymentOption.stripeFee"
                                info="The fee is paid to the payment processing company and is not
                            collected by Rollo."
                                fixedValue={0}
                              />
                            </GroupItem>
                            <GroupItem wide>
                              <LabeledPriceField
                                classes={{
                                  root: classes.calcRow,
                                  price: classes.calcTotal,
                                }}
                                label="Total pay"
                                record={values}
                                source="paymentOption.totalAmount"
                              />
                            </GroupItem>
                          </GroupContent>
                        )}

                        {!!bankAccounts && (
                          <SavedBankAccounts
                            bankAccounts={bankAccounts}
                            defaultAccount={
                              defaultAccount && bankAccounts?.length ? defaultAccount : NEW_ACCOUNT
                            }
                            removeAccount={removeAccount}
                            removing={removingAccount}
                            pickedAccount={pickedAccount}
                            setPickedAccount={setPickedAccount}
                          />
                        )}
                      </>
                    )}
                  </>
                )}

                <FormErrorLabel {...renderProps} />

                <Box className={classes.actions}>
                  <Grid container spacing={4}>
                    <Grid item xs={!settingsError ? 6 : 12}>
                      <Button
                        fullWidth
                        variant="contained"
                        color={!settingsError ? 'secondary' : 'primary'}
                        size="large"
                        onClick={handleClose}
                        className={classes.dialogButton}>
                        Cancel
                      </Button>
                    </Grid>

                    {!settingsError && (
                      <Grid item xs={6}>
                        <SubmitButton
                          fullWidth
                          variant="contained"
                          color="primary"
                          size="large"
                          label={pageType === SINGLE_PAYMENT_TAB ? 'Pay by Bank' : 'Submit'}
                          type="submit"
                          submitting={submitting || settingsLoading || processing}
                          progress={processing ? paymentLoader : undefined}
                          uppercase={false}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </form>
            </>
          );
        }}
      />
    </Box>
  );
};

const ACH = ({ stripePromise, pageTitle, ...props }) => {
  if (pageTitle === ADD_BALANCE || pageTitle === ADD_ACCOUNT) {
    return null;
  }

  return (
    <Box>
      <Elements stripe={stripePromise}>
        <ACHInner {...props} />
      </Elements>
    </Box>
  );
};

export default ACH;
